import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ObliqueModal - Radix UI Headless Dialog wrapped in Oblique design language.
 * Guarantees keyboard accessibility (Esc to close, tab lock) & correct z-index stacking.
 */
export function Modal({
    open,
    onOpenChange,
    title,
    description,
    children,
    footer,
    maxWidth = 'max-w-lg',
    className = ''
}) {
    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <AnimatePresence>
                {open && (
                    <DialogPrimitive.Portal forceMount>
                        {/* Backdrop Overlay */}
                        <DialogPrimitive.Overlay asChild forceMount>
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 bg-black/40 backdrop-blur-sm"
                                style={{ zIndex: 'var(--z-modal-backdrop, 1600)' }}
                            />
                        </DialogPrimitive.Overlay>

                        {/* Modal Content */}
                        <div 
                            className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
                            style={{ zIndex: 'var(--z-modal, 1700)' }}
                        >
                            <DialogPrimitive.Content asChild forceMount>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9, y: 20, filter: 'blur(8px)' }}
                                    animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }}
                                    exit={{ opacity: 0, scale: 0.9, y: 20, filter: 'blur(8px)' }}
                                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                    className={`
                                        relative w-full ${maxWidth} bg-[var(--bg-surface)] border border-[var(--border-strong)] 
                                        rounded-3xl shadow-2xl p-6 sm:p-8 overflow-hidden ${className}
                                    `}
                                >
                                    {/* Header */}
                                    {(title || description) && (
                                        <div className="mb-6 pr-8">
                                            {title && (
                                                <DialogPrimitive.Title className="text-lg font-semibold text-[var(--text-primary)] tracking-tight">
                                                    {title}
                                                </DialogPrimitive.Title>
                                            )}
                                            {description && (
                                                <DialogPrimitive.Description className="mt-1 text-xs text-[var(--text-tertiary)] leading-relaxed">
                                                    {description}
                                                </DialogPrimitive.Description>
                                            )}
                                        </div>
                                    )}

                                    {/* Close Icon Button */}
                                    <DialogPrimitive.Close 
                                        className="absolute top-5 right-5 p-2 rounded-xl text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)] transition-colors"
                                    >
                                        <X size={18} />
                                    </DialogPrimitive.Close>

                                    {/* Body */}
                                    <div className="relative">
                                        {children}
                                    </div>

                                    {/* Footer Actions */}
                                    {footer && (
                                        <div className="mt-6 pt-4 border-t border-[var(--border-subtle)] flex items-center justify-end gap-3">
                                            {footer}
                                        </div>
                                    )}
                                </motion.div>
                            </DialogPrimitive.Content>
                        </div>
                    </DialogPrimitive.Portal>
                )}
            </AnimatePresence>
        </DialogPrimitive.Root>
    );
}
